import { Text } from '@belong/ui';
import { clsx } from 'clsx';
import { ResolveModalFooter } from '../resolve-modal-footer';
import { formatNegativeFailedPaymentsAmount } from '../failed-payments-utils';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';

type ResolutionSuccessRow = {
  key: string;
  label: string;
  status?: string;
  statusColorClassName?: string;
  amount: number;
  note?: string;
};

export type ResolutionSuccessModel = {
  title: string;
  subtitle?: string;
  reference?: string;
  rows: ResolutionSuccessRow[];
  totalAmount?: number;
};

type ResolutionSuccessStepProps = {
  success: ResolutionSuccessModel;
  onDone: () => void;
};

export function ResolutionSuccessStep({
  success,
  onDone,
}: ResolutionSuccessStepProps) {
  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-xs">
        <Text
          variant="h2"
          fontWeight="semibold"
          className="whitespace-pre-line px-2sm text-center md:px-md"
        >
          {success.title}
        </Text>
        {success.subtitle ? (
          <Text variant="p1" className="text-center text-dark-gray">
            {success.subtitle}
          </Text>
        ) : null}
      </div>

      <div className="flex flex-col gap-sm">
        {success.reference ? (
          <>
            <Text fontWeight="semibold" className="text-navy">
              {success.reference}
            </Text>
            <div className="border-0 border-t border-dashed border-gray" />
          </>
        ) : null}

        <div className="flex flex-col gap-sm">
          {success.rows.map((row) => (
            <div
              key={row.key}
              className="flex items-start justify-between gap-md"
            >
              <span className="flex min-w-0 flex-1 flex-col gap-2xs">
                <Text fontWeight="semibold" className="truncate text-navy">
                  {row.label}
                </Text>
                {row.status ? (
                  <Text
                    variant="p1"
                    fontWeight="semibold"
                    className={clsx(row.statusColorClassName ?? 'text-orange')}
                  >
                    {row.status}
                  </Text>
                ) : null}
              </span>
              <span className="flex shrink-0 flex-col items-end gap-2xs">
                <Text fontWeight="semibold" className="text-right text-navy">
                  {formatNegativeFailedPaymentsAmount(row.amount)}
                </Text>
                {row.note ? (
                  <Text variant="p1" className="text-right text-dark-gray">
                    {row.note}
                  </Text>
                ) : null}
              </span>
            </div>
          ))}
        </div>

        {success.totalAmount != null ? (
          <div className="flex items-center justify-between border-0 border-t border-dashed border-gray pt-sm">
            <Text fontWeight="semibold">
              {FAILED_PAYMENTS_STRINGS['section.total']}
            </Text>
            <Text fontWeight="semibold" className="text-right">
              {formatNegativeFailedPaymentsAmount(success.totalAmount)}
            </Text>
          </div>
        ) : null}
      </div>

      <ResolveModalFooter
        onSubmit={onDone}
        submitLabel={FAILED_PAYMENTS_STRINGS['modal.footer.done']}
      />
    </div>
  );
}
